import React, { useState, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setOrganization, updateUserPermissions } from "../../js/actions";
import apiHandler from "../../js/apiHandler";
import { CHANNELS } from "../../js/constants";
import { persistOrganizationContext } from "../../helpers/helpers";

const CHANNEL_ORG_NAMES = new Set(CHANNELS.map((c) => c.value));

const PAGE_SIZE = 10;

function filterChannelOrgs(list) {
	if (!list) return [];
	return list
		.filter((item) => !CHANNEL_ORG_NAMES.has(item.organization_name) && !item.organization_name?.startsWith("tpro") && item.organization_name !== "travelagencies")
		.map((item) => ({
			...item,
			child_organizations: filterChannelOrgs(item.child_organizations),
		}));
}

function flattenOrgTree(list, result = []) {
	if (!list) return result;
	for (const item of list) {
		result.push(item);
		if (item.child_organizations?.length) {
			flattenOrgTree(item.child_organizations, result);
		}
	}
	return result;
}

function itemMatchesQuery(item, q) {
	const orgName = item.organization_name;
	const orgTitle = item.title || orgName;
	if (
		orgTitle.toLowerCase().includes(q) ||
		orgName.toLowerCase().includes(q) ||
		String(item.manager_uid).toLowerCase() === q
	) {
		return true;
	}
	return item.child_organizations?.some((child) => itemMatchesQuery(child, q)) ?? false;
}

function OrgItem({ item, query, currentOrg, onSelect }) {
	const orgName = item.organization_name;
	const orgTitle = item.title || orgName;
	const orgId = item.organization_id;
	const hasChildren = item.child_organizations?.length > 0;

	const q = query.toLowerCase();
	const highlight =
		orgTitle.toLowerCase().includes(q) ||
		orgName.toLowerCase().includes(q) ||
		String(item.manager_uid).toLowerCase() === q;

	const matchingChildren = hasChildren
		? item.child_organizations.filter((child) => itemMatchesQuery(child, q))
		: [];

	if (!highlight && matchingChildren.length === 0) return null;

	const childItems = matchingChildren.map((child, i) => (
		<OrgItem
			key={`${child.organization_id ?? child.organization_name}-${i}`}
			item={child}
			query={query}
			currentOrg={currentOrg}
			onSelect={onSelect}
		/>
	));

	const isActive = currentOrg === orgName;
	const btnClassName = isActive ? "active bold" : highlight ? "bold" : "normal";

	return (
		<li className="isOpen showListItem">
			<button
				type="button"
				className={btnClassName}
				disabled={isActive}
				onClick={isActive ? undefined : () => onSelect(orgName, orgTitle, orgId)}>
				{orgTitle}
			</button>
			{childItems.length > 0 && <ul className="org-nested-list">{childItems}</ul>}
		</li>
	);
}

const OrganizationDropdown = ({ closeModal }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const org = useSelector((state) => state.roleManager.org);
	const user = useSelector((state) => state.authState.user);

	const [query, setQuery] = useState("");
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const inputRef = useRef(null);
	const listRef = useRef(null);

	const orgTree = useMemo(() => {
		const tree = user?.organization_role_tree;
		const globalOrg = tree?.find((o) => o.organization_name === "_global_");
		const raw = globalOrg ? [globalOrg] : tree ?? [];
		return filterChannelOrgs(raw);
	}, [user]);

	const flat = useMemo(() => flattenOrgTree(orgTree), [orgTree]);

	const handleSearch = useCallback((e) => {
		setQuery(e.target.value);
		setVisibleCount(PAGE_SIZE);
	}, []);

	const getFocusableButtons = useCallback(() => {
		if (!listRef.current) return [];
		return Array.from(
			listRef.current.querySelectorAll("li.showListItem > button:not(:disabled)")
		);
	}, []);

	const handleInputKeyDown = useCallback(
		(e) => {
			if (e.key === "ArrowDown") {
				e.preventDefault();
				getFocusableButtons()[0]?.focus();
			}
		},
		[getFocusableButtons],
	);

	const handleListKeyDown = useCallback(
		(e) => {
			if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Home" && e.key !== "End") return;
			e.preventDefault();
			const buttons = getFocusableButtons();
			if (!buttons.length) return;
			const idx = buttons.indexOf(document.activeElement);
			if (e.key === "ArrowDown") {
				(buttons[idx + 1] ?? buttons[0]).focus();
			} else if (e.key === "ArrowUp") {
				if (idx <= 0) {
					inputRef.current?.focus();
				} else {
					buttons[idx - 1].focus();
				}
			} else if (e.key === "Home") {
				buttons[0].focus();
			} else if (e.key === "End") {
				buttons[buttons.length - 1].focus();
			}
		},
		[getFocusableButtons],
	);

	const selectOrg = useCallback(
		async (orgName, orgTitle, orgId) => {
			closeModal();
			navigate("/dashboard");
			dispatch(setOrganization({ org: orgName, orgTitle, orgId }));
			persistOrganizationContext({ org: orgName, orgTitle, orgId });
			window.dispatchEvent(new CustomEvent("organization-switched"));

			try {
				const resp = await apiHandler.get({
					apiName: "accapi",
					path: `/currentuser/${orgName}/permissions`,
				});
				if (Array.isArray(resp?.data?.permissions)) {
					dispatch(updateUserPermissions(resp.data.permissions));
				}
			} catch (err) {
				console.error("Failed to load permissions for org", orgName, err);
			}
		},
		[closeModal, navigate, dispatch],
	);

	const isSearching = query.length >= 1;

	let listContent = null;
	let showMoreButton = null;

	if (orgTree.length) {
		if (isSearching) {
			listContent = orgTree.map((item, i) => (
				<OrgItem
					key={`${item.organization_id ?? item.organization_name}-${i}`}
					item={item}
					query={query}
					currentOrg={org}
					onSelect={selectOrg}
				/>
			));
		} else {
			const visible = flat.slice(0, visibleCount);
			listContent = visible.map((item, i) => {
				const orgName = item.organization_name;
				const orgTitle = item.title || orgName;
				const orgId = item.organization_id;
				const isActive = org === orgName;
				return (
					<li key={`${orgId ?? orgName}-${i}`} className="isOpen showListItem">
						<button
							type="button"
							className={isActive ? "active bold" : "normal"}
							disabled={isActive}
							onClick={isActive ? undefined : () => selectOrg(orgName, orgTitle, orgId)}>
							{orgTitle}
						</button>
					</li>
				);
			});

			if (visibleCount < flat.length) {
				const remaining = flat.length - visibleCount;
				showMoreButton = (
					<li className="org-show-more">
						<button
							type="button"
							onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
							Show {Math.min(remaining, PAGE_SIZE)} more
							<span className="org-show-more-count"> ({remaining} remaining)</span>
						</button>
					</li>
				);
			}
		}
	}

	return (
		<div className="org-dropdown open">
			<input
				ref={inputRef}
				type="text"
				placeholder="Search by Account Name or Id"
				value={query}
				onChange={handleSearch}
				onKeyDown={handleInputKeyDown}
				autoFocus
				className="form-field"
			/>
			{listContent && (
				<ul ref={listRef} className="org-dropdown-list" onKeyDown={handleListKeyDown}>
					{listContent}
					{showMoreButton}
				</ul>
			)}
		</div>
	);
};

export default OrganizationDropdown;
